import { parseInferenceRowKey } from "@/lib/connectors/inference-rows";
import {
	parseSandboxProviderKey,
	parseSandboxRowKey,
} from "@/lib/connectors/sandbox-rows";

/**
 * What a routable connector key actually stands for.
 *
 * Model providers and sandboxes are namespaced INTO the `$connectorKey` param
 * with a `prefix:` because they share that one detail route. Each used to be
 * recognised by its own `parse*Key` call at three separate places — the list
 * synthesis, the detail branch, and the route loader's prefetch guard — and
 * the loader was the one that got forgotten, firing connector-definition
 * prefetches for keys no connector table has heard of.
 *
 * Devices and clients are deliberately NOT here: devices have their own
 * static route (`/connectors/device/{id}`), which keeps their URLs free of a
 * percent-encoded colon; connected clients live on their app page
 * (`/connectors/app/{appId}?client=...`).
 */
type ConnectorRowKind =
	| "connector"
	| "inference"
	| "sandbox"
	| "sandbox-provider";

interface ResolvedRowKind {
	kind: ConnectorRowKind;
	/** The remainder after the prefix; null for a real connector key. */
	id: string | null;
}

export function resolveConnectorRowKind(connectorKey: string): ResolvedRowKind {
	const inference = parseInferenceRowKey(connectorKey);
	if (inference !== null) return { kind: "inference", id: inference };

	const sandbox = parseSandboxRowKey(connectorKey);
	if (sandbox !== null) return { kind: "sandbox", id: sandbox };

	const sandboxProvider = parseSandboxProviderKey(connectorKey);
	if (sandboxProvider !== null) {
		return { kind: "sandbox-provider", id: sandboxProvider };
	}

	return { kind: "connector", id: null };
}

/**
 * Whether this key names a row backed by `connector_definitions` — i.e. the
 * only kind for which prefetching connector defs and connections is useful.
 */
export function isRealConnectorKey(connectorKey: string): boolean {
	return resolveConnectorRowKind(connectorKey).kind === "connector";
}
